<template>
  <div class="page-home min-h-screen">
    <section
      v-for="section in sections"
      :key="section.title"
      class="mx-auto max-w-10xl px-4 pb-6 pt-10 lg:px-8"
    >
      <div class="mb-4 flex items-center justify-between">
        <RouterLink :to="vendorLink" class="group inline-flex items-center gap-2">
          <h2 class="text-xl font-semibold text-ink transition-colors group-hover:text-brand">
            {{ section.title }}
          </h2>
          <i class="ri-arrow-right-s-line text-lg text-ink transition-all group-hover:translate-x-1 pt-1 group-hover:text-brand" aria-hidden="true"></i>
        </RouterLink>
        <div class="flex items-center gap-2">
          <button :class="`nav-btn prev prev-${section.id}`" aria-label="Previous">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <button :class="`nav-btn next next-${section.id}`" aria-label="Next">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
      <Swiper
        class="stay-swiper"
        :modules="[Navigation]"
        :slides-per-view="1.4"
        :space-between="16"
        :navigation="{ prevEl: `.prev-${section.id}`, nextEl: `.next-${section.id}` }"
        :breakpoints="{
          640: { slidesPerView: 2.4 },
          768: { slidesPerView: 3.2 },
          1024: { slidesPerView: 4.2 },
          1280: { slidesPerView: 5.2 },
          1536: { slidesPerView: 5.8 }
        }"
      >
        <SwiperSlide v-for="item in section.items" :key="item.title">
          <article class="stay-card">
            <div class="stay-img">
              <img :src="item.img" :alt="item.title">
              <button class="fav-btn" aria-label="Save">
                <i class="ri-heart-line"></i>
              </button>
              <span v-if="item.badge" class="badge">{{ item.badge }}</span>
            </div>
            <div class="stay-meta">
              <h3 class="title">{{ item.title }}</h3>
              <p class="price">{{ item.price }}</p>
              <p class="rating">
                <i class="ri-star-fill text-amber-500"></i>
                <span>{{ item.rating }}</span>
                <span v-if="item.tag" class="tag">• {{ item.tag }}</span>
              </p>
            </div>
          </article>
        </SwiperSlide>
      </Swiper>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import sections from '@/data/home-sections'
import 'swiper/css'
import 'swiper/css/navigation'

const formatDate = (year, month, day) => {
  const date = new Date(Date.UTC(year, month, day))
  return date.toISOString().split('T')[0]
}

const vendorLink = computed(() => {
  const today = new Date()
  const currentYear = today.getFullYear()
  const checkin = formatDate(currentYear, today.getMonth(), 1)
  const checkout = formatDate(currentYear, 11, 31)

  return {
    path: '/vendors/all',
    query: {
      date_picker_type: 'CALENDAR',
      search_type: 'HOMEPAGE',
      checkin,
      checkout
    }
  }
})
</script>

<style scoped>
.stay-card {
  background: #fff;
  border: 1px solid #eaecef;
  border-radius: 22px;
  overflow: hidden;
  transition: transform 0.18s ease, border-color 0.18s ease;
  min-height: 270px;
}

.stay-card:hover {
  transform: translateY(-3px);
  border-color: #d5d9e2;
}

.stay-img {
  position: relative;
  height: 180px;
}

.stay-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fav-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #0f172a;
  border: none;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.stay-meta {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stay-meta .title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.stay-meta .price {
  font-size: 13px;
  color: #6b7280;
}

.stay-meta .rating {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
}

.stay-meta .tag {
  color: #6b7280;
}

.nav-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: #f2f3f5;
  color: #0f172a;
  transition: transform 0.15s ease, background 0.15s ease;
}

.nav-btn:hover {
  transform: translateY(-1px);
  background: #e5e7eb;
}

.stay-swiper :global(.swiper-slide) {
  height: auto;
}
</style>
